/* Brand ramp derivation for per-kitchen theming.
 *
 * A kitchen supplies one hex (brand_primary). We derive the full interactive
 * ramp in OKLCH so hue stays stable while lightness moves, then clamp the
 * base color dark enough that white text always meets WCAG AA (4.5:1).
 */

const FALLBACK = '#0e6b43'

type Oklch = { l: number; c: number; h: number }

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055
}

export function parseHex(hex: string): [number, number, number] | null {
  const value = hex.trim().replace(/^#/, '')
  const full =
    value.length === 3
      ? value
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : value
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ]
}

function toHex([r, g, b]: [number, number, number]): string {
  const channel = (c: number) =>
    Math.round(Math.min(1, Math.max(0, c)) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

function rgbToOklch([r, g, b]: [number, number, number]): Oklch {
  const lr = srgbToLinear(r)
  const lg = srgbToLinear(g)
  const lb = srgbToLinear(b)
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return {
    l: okL,
    c: Math.hypot(okA, okB),
    h: (Math.atan2(okB, okA) * 180) / Math.PI,
  }
}

function oklchToRgbUnclamped({ l, c, h }: Oklch): [number, number, number] {
  const a = c * Math.cos((h * Math.PI) / 180)
  const b = c * Math.sin((h * Math.PI) / 180)
  const l1 = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m1 = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s1 = (l - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    linearToSrgb(4.0767416621 * l1 - 3.3077115913 * m1 + 0.2309699292 * s1),
    linearToSrgb(-1.2684380046 * l1 + 2.6097574011 * m1 - 0.3413193965 * s1),
    linearToSrgb(-0.0041960863 * l1 - 0.7034186147 * m1 + 1.707614701 * s1),
  ]
}

function inGamut(rgb: [number, number, number]): boolean {
  return rgb.every((c) => c >= -0.0001 && c <= 1.0001)
}

/* Reduce chroma until the color fits sRGB, preserving lightness and hue. */
function oklchToHex(color: Oklch): string {
  let rgb = oklchToRgbUnclamped(color)
  if (inGamut(rgb)) return toHex(rgb)
  let low = 0
  let high = color.c
  for (let i = 0; i < 18; i += 1) {
    const mid = (low + high) / 2
    rgb = oklchToRgbUnclamped({ ...color, c: mid })
    if (inGamut(rgb)) low = mid
    else high = mid
  }
  return toHex(oklchToRgbUnclamped({ ...color, c: low }))
}

function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 0
  const [r, g, b] = rgb.map(srgbToLinear)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

export type BrandRamp = {
  brand: string
  brandStrong: string
  brandPressed: string
  brandTint: string
  brandTintStrong: string
  onBrand: string
  /* AA text color for copy sitting on brandTint. */
  brandOnTint: string
}

export function deriveBrandRamp(
  brandPrimary: string | null | undefined,
): BrandRamp {
  const parsed = parseHex(brandPrimary ?? '') ?? parseHex(FALLBACK)!
  const source = rgbToOklch(parsed)

  /* Base: keep the kitchen's hue, but force AA contrast for white text. */
  const base: Oklch = {
    l: Math.min(Math.max(source.l, 0.3), 0.62),
    c: Math.min(source.c, 0.17),
    h: source.h,
  }
  let brand = oklchToHex(base)
  while (contrastRatio(brand, '#ffffff') < 4.5 && base.l > 0.2) {
    base.l -= 0.01
    brand = oklchToHex(base)
  }

  const tintC = Math.min(base.c * 0.28, 0.04)
  const brandTint = oklchToHex({ l: 0.962, c: tintC, h: base.h })
  const onTint: Oklch = { l: 0.32, c: Math.min(base.c, 0.12), h: base.h }
  let brandOnTint = oklchToHex(onTint)
  while (contrastRatio(brandOnTint, brandTint) < 4.5 && onTint.l > 0.15) {
    onTint.l -= 0.01
    brandOnTint = oklchToHex(onTint)
  }
  return {
    brand,
    brandStrong: oklchToHex({ ...base, l: base.l - 0.055 }),
    brandPressed: oklchToHex({ ...base, l: base.l - 0.1 }),
    brandTint,
    brandTintStrong: oklchToHex({
      l: 0.925,
      c: Math.min(base.c * 0.4, 0.06),
      h: base.h,
    }),
    onBrand: '#ffffff',
    brandOnTint,
  }
}

/* Dark-theme counterpart: the brand lightens into a mint/pastel so it reads
   as text on dark surfaces; button text flips to a deep hue-tinted ink. */
export function deriveBrandDarkRamp(
  brandPrimary: string | null | undefined,
): BrandRamp {
  const parsed = parseHex(brandPrimary ?? '') ?? parseHex(FALLBACK)!
  const source = rgbToOklch(parsed)
  const darkSurface = '#17231b'

  const base: Oklch = {
    l: Math.min(Math.max(source.l, 0.68), 0.76),
    c: Math.min(source.c, 0.14),
    h: source.h,
  }
  let brand = oklchToHex(base)
  while (contrastRatio(brand, darkSurface) < 4.5 && base.l < 0.9) {
    base.l += 0.01
    brand = oklchToHex(base)
  }

  const brandTint = oklchToHex({
    l: 0.28,
    c: Math.min(base.c * 0.5, 0.05),
    h: base.h,
  })
  const onTint: Oklch = { l: 0.85, c: Math.min(base.c * 0.6, 0.08), h: base.h }
  let brandOnTint = oklchToHex(onTint)
  while (contrastRatio(brandOnTint, brandTint) < 4.5 && onTint.l < 0.95) {
    onTint.l += 0.01
    brandOnTint = oklchToHex(onTint)
  }
  return {
    brand,
    brandStrong: oklchToHex({ ...base, l: base.l - 0.06 }),
    brandPressed: oklchToHex({ ...base, l: base.l - 0.11 }),
    brandTint,
    brandTintStrong: oklchToHex({
      l: 0.35,
      c: Math.min(base.c * 0.55, 0.06),
      h: base.h,
    }),
    onBrand: oklchToHex({
      l: 0.22,
      c: Math.min(base.c * 0.5, 0.06),
      h: base.h,
    }),
    brandOnTint,
  }
}

export type AccentRamp = {
  accent: string
  accentTint: string
  accentStrong: string
  onAccent: string
}

/* Warm accent derived per kitchen: the burnished-amber anchor leans toward
   the brand hue, so each kitchen's gold carries its identity while staying
   in the warm band that reads as an accent, never a status. */
const AMBER_HUE = 75

function mixHueTowards(from: number, to: number, weight: number): number {
  const delta = ((to - from + 540) % 360) - 180
  return (from + delta * weight + 360) % 360
}

export function deriveAccentRamp(
  brandPrimary: string | null | undefined,
  theme: 'light' | 'dark',
): AccentRamp {
  const parsed = parseHex(brandPrimary ?? '') ?? parseHex(FALLBACK)!
  const hue = mixHueTowards(AMBER_HUE, rgbToOklch(parsed).h, 0.3)

  if (theme === 'dark') {
    const accentTint = oklchToHex({ l: 0.28, c: 0.05, h: hue })
    const strong: Oklch = { l: 0.84, c: 0.1, h: hue }
    let accentStrong = oklchToHex(strong)
    while (contrastRatio(accentStrong, accentTint) < 4.5 && strong.l < 0.95) {
      strong.l += 0.01
      accentStrong = oklchToHex(strong)
    }
    return {
      accent: oklchToHex({ l: 0.8, c: 0.12, h: hue }),
      accentTint,
      accentStrong,
      onAccent: oklchToHex({ l: 0.24, c: 0.05, h: hue }),
    }
  }

  const accentTint = oklchToHex({ l: 0.965, c: 0.035, h: hue })
  const strong: Oklch = { l: 0.52, c: 0.11, h: hue }
  let accentStrong = oklchToHex(strong)
  while (contrastRatio(accentStrong, accentTint) < 4.5 && strong.l > 0.3) {
    strong.l -= 0.01
    accentStrong = oklchToHex(strong)
  }
  return {
    accent: oklchToHex({ l: 0.75, c: 0.13, h: hue }),
    accentTint,
    accentStrong,
    onAccent: oklchToHex({ l: 0.3, c: 0.07, h: hue }),
  }
}
